/**
 * RerunFreshnessList — pre-rerun review surface (Reader Polish Prompt 8
 * + Apply-on-Rerun pack D6).
 *
 * Two stacked cards:
 *
 *   1. (Apply-on-Rerun, D6) A cyan "queued apply" card above the
 *      freshness list when the parent report carries any sanity
 *      findings resolved as `apply` — shows the queued directives
 *      (edited text where the user edited the suggestion). This is
 *      the user's preview of "what this rerun will rewrite". It
 *      renders independently of freshness state.
 *
 *   2. (Reader Polish Prompt 8) A lightweight stale-section list with
 *      a single "Skip refresh →" text link; the actual refresh /
 *      generate decision is driven by the panel's footer CTA whose
 *      label flips based on `skipRefresh`.
 *
 * Per-row copy resolves the legacy "MISSING · 1 day old" mismatch:
 *   - section has a retrievedAt but no data → "last attempted N
 *     day(s) ago, no data returned".
 *   - section has no retrievedAt at all     → "never collected".
 *
 * Returns null only when there is nothing to show — no stale required
 * sections AND no queued apply findings.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { EST_PROVIDER_COST_USD, providersForSections, type SedSectionStatus } from '../../../lib/sedStaleness';
import type { SanityFinding } from '../../../types/siteRag';
import { useIsDatacrateAdmin } from '../../../lib/isDatacrateAdmin';

export interface RerunFreshnessListProps {
  staleStatuses: SedSectionStatus[];
  /** All section statuses for the SED — drives the cost helper's
   *  provider de-dup. The list itself only renders the stale rows. */
  allStatuses: SedSectionStatus[];
  reportType: string;
  skipRefresh: boolean;
  onToggleSkip: () => void;
  /**
   * Apply-on-Rerun pack (D6) — the parent report's sanity findings
   * resolved as `apply`. When non-empty a cyan preview card listing
   * the queued directives (edited text where present) renders above
   * the freshness list. Defaults to undefined (no card).
   */
  queuedApplyFindings?: SanityFinding[];
}

export function describeFreshnessRow(status: SedSectionStatus): string {
  // The status badge still reads 'missing' / 'stale'; the row copy
  // separates the two actual cases:
  if (status.lastFetchedAt) {
    const days = status.ageDays ?? 0;
    if (status.staleness === 'missing') {
      // Has a timestamp + 'missing' classification = the section
      // tried to fetch but came back empty.
      return `last attempted ${days} day${days === 1 ? '' : 's'} ago, no data returned`;
    }
    return `${days} day${days === 1 ? '' : 's'} old · TTL ${status.ttlDays}d`;
  }
  // No timestamp — never collected for this site.
  return 'never collected';
}

/** The text shown for a queued directive — the user's edit if they
 *  edited the suggestion, else the snapshotted directive text, else
 *  the finding text itself. Truncated for the preview list. */
function queuedDirectiveText(f: SanityFinding): string {
  const raw = (f.userNote && f.userNote.trim().length > 0)
    ? f.userNote.trim()
    : (f.applyDirective?.suggestionAtResolution && f.applyDirective.suggestionAtResolution.trim().length > 0)
      ? f.applyDirective.suggestionAtResolution.trim()
      : (f.suggestion?.trim() || f.finding);
  return raw.length > 120 ? `${raw.slice(0, 120)}…` : raw;
}

/** The section a queued directive targets — the directive's snapshot
 *  section wins over the finding's section. */
function queuedDirectiveSection(f: SanityFinding): string {
  return f.applyDirective?.section?.trim() || f.section;
}

export function RerunFreshnessList({
  staleStatuses,
  allStatuses,
  reportType,
  skipRefresh,
  onToggleSkip,
  queuedApplyFindings,
}: RerunFreshnessListProps) {
  // Provider $ cost figure is Datacrate admin-only telemetry. The
  // cost MATH still runs below; only the rendered $ is gated.
  const isDatacrateAdmin = useIsDatacrateAdmin();
  const queued = (queuedApplyFindings ?? []).filter(f => f.resolution === 'apply');
  const queuedApplyCount = queued.length;
  const hasStale = staleStatuses.length > 0;

  // Nothing to surface at all.
  if (!hasStale && queuedApplyCount === 0) return null;

  const providerCount = hasStale
    ? providersForSections(allStatuses, staleStatuses.map(s => s.id)).length
    : 0;
  const refreshCostUsd = providerCount * EST_PROVIDER_COST_USD;

  return (
    <div className="space-y-3" data-testid="rerun-review-surface">
      {queuedApplyCount > 0 && (
        <div
          // Original cyan-300 on bg-cyan-500/10 was washed out in light mode
          // (cyan-300 = light cyan, designed for dark bg; bg-cyan-500/10 on a
          // light card = light-on-light). Swapped to the codebase's semantic
          // `info` token which mirrors the `text-warning` / `bg-warning/5`
          // pattern in the sibling block below — theme handles contrast in
          // both light and dark mode.
          className="px-5 py-4 border border-info/40 bg-info/5 space-y-1.5"
          data-testid="rerun-queued-apply-card"
          data-queued-count={queuedApplyCount}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-info shrink-0" aria-hidden="true" />
            <div className="type-section text-info">
              {queuedApplyCount} finding{queuedApplyCount === 1 ? '' : 's'} will be applied on this rerun
            </div>
          </div>
          <ul className="text-[10px] text-text-secondary space-y-1 pl-6 list-disc">
            {queued.slice(0, 5).map(f => (
              <li key={f.id} data-testid={`rerun-queued-apply-row-${f.id}`}>
                <span className="text-text-primary font-semibold">{queuedDirectiveSection(f)}:</span>{' '}
                {queuedDirectiveText(f)}
              </li>
            ))}
            {queued.length > 5 && (
              <li className="italic text-text-muted" data-testid="rerun-queued-apply-more">
                + {queued.length - 5} more
              </li>
            )}
          </ul>
        </div>
      )}

      {hasStale && (
        <div
          className="px-5 py-4 rounded-md border border-warning/40 bg-warning/5 space-y-3"
          data-testid="rerun-freshness-list"
          data-skip-refresh={skipRefresh}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" aria-hidden="true" />
            <div className="space-y-1 flex-1 min-w-0">
              <h3 className="type-section text-warning">
                Evidence freshness for this {reportType} report
              </h3>
              <p className="text-[10px] text-text-secondary">
                {staleStatuses.length} required section{staleStatuses.length === 1 ? '' : 's'} stale or missing.
              </p>
            </div>
          </div>
          <ul className="space-y-1 pl-6">
            {staleStatuses.map(s => (
              <li
                key={s.id}
                data-testid={`rerun-freshness-row-${s.id}`}
                className="text-[10px] flex items-center justify-between gap-2"
              >
                <span className="font-semibold text-text-primary">{s.label}</span>
                <span className="text-text-muted text-right" data-testid={`rerun-freshness-row-${s.id}-detail`}>
                  {describeFreshnessRow(s)}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-2 pt-1 border-t border-warning/20">
            {isDatacrateAdmin ? (
              <span className="type-body text-text-muted uppercase tracking-widest" data-testid="rerun-freshness-cost">
                {refreshCostUsd > 0 ? `~$${refreshCostUsd.toFixed(2)} provider cost` : 'no extra cost'}
              </span>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={onToggleSkip}
              className="text-xs text-text-muted hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              data-testid="rerun-freshness-skip"
              aria-pressed={skipRefresh}
            >
              {skipRefresh ? 'Refresh on rerun ←' : 'Skip refresh →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
